import { CircleNotch } from "@phosphor-icons/react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  getApiErrorMessage,
  type HouseholdDocumentInfo,
  useDeleteHouseholdDocument,
} from "@/lib/api";

interface Props {
  householdId: string;
  document: HouseholdDocumentInfo | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DocumentDeleteDialog({ householdId, document, open, onOpenChange }: Props) {
  const deleteMutation = useDeleteHouseholdDocument();
  const [error, setError] = useState("");

  const handleDelete = async () => {
    if (!document) {
      return;
    }
    setError("");
    try {
      await deleteMutation.mutateAsync({ householdId, documentId: document.id });
      onOpenChange(false);
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to delete document."));
    }
  };

  // Clear any stale error whenever the dialog opens or closes, so a failed
  // delete doesn't leave its message behind when the dialog is reopened.
  const handleOpenChange = (nextOpen: boolean) => {
    setError("");
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete document</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete <strong>{document?.title}</strong>? This action cannot
            be undone.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? (
              <CircleNotch className="h-4 w-4 animate-spin" weight="light" />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
